import React, { useState } from "react";
import { useGlobalStore } from "~/stores/store-provider";

const NoDocument = ({ sessionId }: { sessionId: string }) => {
  const {
    createBlankDocument,
    generateDocumentWithAI,
  } = useGlobalStore((state: any) => state);

  const [showPromptInput, setShowPromptInput] = useState(false);
  const [userPrompt, setUserPrompt] = useState("");
  const [error, setError] = useState("");

  const isValid = userPrompt.trim().length > 0;

  const handleCreateDocument = async () => {
    try {
      await createBlankDocument(sessionId);
    } catch (error) {
      console.error('Failed to create document:', error);
    }
  };

  const handleAIButtonClick = () => {
    setShowPromptInput(true);
  };

  const handleGenerate = async () => {
    if (!isValid) {
      setError("Please provide instructions for the document");
      return;
    }
    
    try {
      await generateDocumentWithAI(sessionId, userPrompt.trim());
      // UI will automatically update via store
    } catch (error) {
      console.error('Failed to generate document:', error);
      setError("Failed to generate document. Please try again.");
    }
  };

  const handleCancel = () => {
    setShowPromptInput(false);
    setUserPrompt("");
    setError("");
  };

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUserPrompt(e.target.value);
    if (error) setError("");
  };
  
  return (
    <div className="h-full w-full flex items-center justify-center">
      <div className="text-center max-w-lg mx-auto p-8 rounded-lg shadow-sm">
        <div className="text-6xl mb-4">📄</div>
        <h3 className="text-xl font-semibold mb-2">No analysis document yet</h3>
        <p className="mb-6">Start documenting your analysis and insights</p>
        
        {!showPromptInput ? (
          <button 
            onClick={handleAIButtonClick}
            className="btn btn-primary"
          >
            Generate Document with AI
          </button>
        ) : (
          <div className="space-y-3 text-left">
            <div>
              <label className="label">
                <span className="label-text">
                  Tell the AI what kind of document to create
                  <span className="text-error ml-1">*</span>
                </span>
              </label>
              <textarea
                autoFocus
                value={userPrompt}
                onChange={handlePromptChange}
                placeholder="Be specific about what you want..."
                className={`textarea textarea-bordered w-full ${error ? 'textarea-error' : ''}`}
                rows={3}
              />
              {error && (
                <label className="label">
                  <span className="label-text-alt text-error">{error}</span>
                </label>
              )}
            </div>
            
            <div className="text-sm text-gray-500">
              Examples:
              <ul className="mt-1 ml-4 list-disc text-xs">
                <li>Create an executive summary with key findings and recommendations</li>
                <li>Generate a technical report with implementation details and code</li>
                <li>Write a presentation outline for stakeholders</li>
                <li>Document the analysis methodology and results</li>
              </ul>
            </div>
            
            <div className="flex gap-2 justify-center">
              <button 
                onClick={handleGenerate}
                disabled={!isValid}
                className="btn btn-primary"
                title={!isValid ? "Please enter instructions" : ""}
              >
                Generate
              </button>
              <button 
                onClick={handleCancel}
                className="btn btn-ghost"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        
        <div className="divider">OR</div>
        <button 
          onClick={handleCreateDocument}
          className="btn btn-outline"
        >
          Create Blank Document
        </button>
      </div>
    </div>
  );
};

export default NoDocument;
